/* eslint-disable max-lines */
import { alpha } from '@mui/material';
import { CSSProperties } from 'react';

type ButtonPalette = {
  primary: string;
  secondary: string;
  accent: string;
  landing: string;
};

type ForegroundColors = {
  default?: string;
  light?: string;
  paper?: string;
  contrast?: string;
  accent?: string;
  warning?: string;
  error?: string;
};

declare module '@mui/material/styles' {
  interface Palette {
    page: {
      headerText: {
        default: string;
        accent: string;
      };
    };
    modal: {
      background: {
        card: string;
      };
    };
    header: {
      primary: string;
      secondary: string;
    };
    shadow: {
      dropdown: CSSProperties['boxShadow'];
      modal: CSSProperties['boxShadow'];
    };
    button: {
      background: ButtonPalette;
      border: ButtonPalette;
      hover: ButtonPalette;
      icon: ButtonPalette;
      text: ButtonPalette;
      outline: string;
    };
    icon: {
      default: string;
      error: string;
      info: string;
      success: string;
      warning: string;
      disabled: string;
      sideNav: string;
    };
    foreground: {
      default: ForegroundColors;
      paper: ForegroundColors;
      paperCard: ForegroundColors;
      paperHeader: ForegroundColors;
    };
    agentChat: {
      message: {
        button: {
          primary: {
            hover: {
              background: string;
              border: string;
              foreground: string;
            };
            background: string;
            border: string;
            foreground: string;
          };
          secondary: {
            hover: {
              background: string;
              border: string;
              foreground: string;
            };
            background: string;
            border: string;
            foreground: string;
          };
          destructive: {
            hover: {
              background: string;
              border: string;
              foreground: string;
            };
            background: string;
            border: string;
            foreground: string;
          };
        };
        agent: {
          background: string;
          backgroundHover: string;
          backgroundSelected: string;
          border: string;
          foreground: {
            default: string;
            light: string;
          };
        };
        user: {
          background: string;
          border: string;
          foreground: {
            default: string;
            light: string;
          };
          queued: {
            background: string;
            border: string;
            foreground: {
              default: string;
              light: string;
              warning: string;
            };
          };
        };
      };
    };
    backgroundHover: {
      default: string;
      paper: string;
      paperCard: string;
    };
    input: {
      background: {
        default: string;
        hover: string;
        active: string;
        disabled: string;
      };
      border: {
        default: string;
        hover: string;
        active: string;
        disabled: string;
        error: string;
      };
      text: {
        default: string;
        disabled: string;
        error: string;
        placeholder: string;
      };
      icon: {
        default: string;
        disabled: string;
      };
      label: {
        default: string;
        error: string;
        disabled: string;
      };
    };
    toggle: {
      thumb: string;
      track: {
        unchecked: {
          default: string;
          hover: string;
          border: string;
        };
        checked: {
          default: string;
          hover: string;
          border: string;
        };
      };
    };
    radio: {
      fill: {
        checked: string;
        unchecked: string;
      };
      default: string;
      hover: string;
      focus: string;
      icon: {
        default: string;
        hover: string;
        checked: string;
        checkedBackground: string;
      };
    };
    checkbox: {
      checked: {
        background: string;
        border: string;
        hover: string;
      };
      unchecked: {
        background: string;
        border: string;
        hover: string;
      };
    };
    menu: {
      background: string;
      border: string;
      item: {
        hover: string;
        selected: string;
        hoverText: string;
      };
    };
    progress: {
      track: string;
      default: string;
      error: string;
      warning: string;
    };
    tab: {
      unselected: {
        default: string;
        hover: string;
      };
      selected: {
        default: string;
        hover: string;
      };
      disabled: string;
      indicator: string;
      divider: string;
    };
    link: {
      default: string;
      hover: string;
    };
    graph: {
      link: {
        default: string;
        inactive: string;
      };
      port: {
        linked: string;
        unlinked: string;
        background: {
          input: string;
          inputHover: string;
          output: string;
        };
      };
      node: {
        background: string;
        border: {
          default: string;
          hover: string;
          active: string;
          error: string;
        };
      };
      drawer: {
        background: string;
      };
    };
    table: {
      header: string;
      cell: {
        border: string;
        text: string;
      };
      row: {
        hover: string;
        selected: string;
      };
      modeButton: {
        hover: string;
        border: {
          default: string;
          selected: string;
        };
        icon: {
          default: string;
          hover: string;
          selected: string;
        };
        background: {
          default: string;
          selected: string;
          hover: string;
        };
      };
    };
    slider: {
      thumb: {
        border: string;
        background: {
          default: string;
          hover: string;
        };
        active: string;
      };
      track: {
        default: string;
        disabled: string;
      };
      rail: {
        default: string;
        disabled: string;
      };
    };
    fileUpload: {
      background: {
        default: string;
        hover: string;
      };
      border: {
        default: string;
        drag: string;
      };
      file: {
        background: string;
        color: {
          primary: string;
          secondary: string;
        };
      };
    };
    resource: {
      workspace: TypeResource;
      organization: TypeResource;
      user: TypeResource;
      platform: TypeResource;
      service: TypeResource;
    };
  }

  interface TypeResource {
    foreground: string;
    background: string;
    border: string;
    hoverBorder: string;
  }

  interface TypeText {
    default: string;
    light: string;
    paper: string;
    contrast: string;
    accent: string;
    warning: string;
    error: string;
  }

  export interface TypeBackground {
    default: string;
    paper: string;
    paperCard: string;
    paperHeader: string;
  }

  interface PaletteOptions {
    page?: {
      headerText?: {
        default: string;
        accent: string;
      };
    };
    modal?: {
      background: {
        card: string;
      };
    };
    header?: {
      primary: string;
      secondary: string;
    };
    shadow?: {
      dropdown: CSSProperties['boxShadow'];
      modal: CSSProperties['boxShadow'];
    };
    button?: {
      background: ButtonPalette;
      border: ButtonPalette;
      hover: ButtonPalette;
      icon: ButtonPalette;
      text: ButtonPalette;
      outline: string;
    };
    icon?: {
      default: string;
      error: string;
      info: string;
      success: string;
      warning: string;
      disabled: string;
      sideNav: string;
    };
    foreground: {
      default: ForegroundColors;
      paper: ForegroundColors;
      paperCard: ForegroundColors;
      paperHeader: ForegroundColors;
    };
    backgroundHover: {
      default: string;
      paper: string;
      paperCard: string;
    };
    input?: {
      background: {
        default: string;
        hover: string;
        active: string;
        disabled: string;
      };
      border: {
        default: string;
        hover: string;
        active: string;
        disabled: string;
        error: string;
      };
      text: {
        default: string;
        disabled: string;
        error: string;
        placeholder: string;
      };
      icon: {
        default: string;
        disabled: string;
      };
      label: {
        default: string;
        error: string;
        disabled: string;
      };
    };
    toggle?: {
      thumb: string;
      track: {
        unchecked: {
          default: string;
          hover: string;
          border: string;
        };
        checked: {
          default: string;
          hover: string;
          border: string;
        };
      };
    };
    radio?: {
      fill: {
        checked: string;
        unchecked: string;
      };
      default: string;
      hover: string;
      focus: string;
      icon: {
        default: string;
        hover: string;
        checked: string;
        checkedBackground: string;
      };
    };
    checkbox?: {
      checked: {
        background: string;
        border: string;
        hover: string;
      };
      unchecked: {
        background: string;
        border: string;
        hover: string;
      };
    };
    menu?: {
      background: string;
      border: string;
      item: {
        hover: string;
        selected: string;
        hoverText: string;
      };
    };
    progress?: {
      track: string;
      default: string;
      error: string;
      warning: string;
    };
    tab?: {
      unselected: {
        default: string;
        hover: string;
      };
      selected: {
        default: string;
        hover: string;
      };
      disabled: string;
      indicator: string;
      divider: string;
    };
    link?: {
      default: string;
      hover: string;
    };
    graph?: {
      link: {
        default: string;
        inactive: string;
      };
      port?: {
        linked: string;
        unlinked: string;
        background: {
          input: string;
          inputHover: string;
          output: string;
        };
      };
      node: {
        background: string;
        border: {
          default: string;
          hover: string;
          active: string;
          error: string;
        };
      };
      drawer: {
        background: string;
      };
    };
    table?: {
      cell: {
        border: string;
        text: string;
      };
      row: {
        hover: string;
        selected: string;
      };
      header: string;
      modeButton: {
        hover: string;
        border: {
          default: string;
          selected: string;
        };
        icon: {
          default: string;
          hover: string;
          selected: string;
        };
        background: {
          default: string;
          selected: string;
          hover: string;
        };
      };
    };
    slider?: {
      thumb: {
        border: string;
        background: {
          default: string;
          hover: string;
        };
        active: string;
      };
      track: {
        default: string;
        disabled: string;
      };
      rail: {
        default: string;
        disabled: string;
      };
    };
    fileUpload?: {
      background: {
        default: string;
        hover: string;
      };
      border: {
        default: string;
        drag: string;
      };
      file: {
        background: string;
        color: {
          primary: string;
          secondary: string;
        };
      };
    };
    resource: {
      workspace: TypeResource;
      organization: TypeResource;
      user: TypeResource;
      platform: TypeResource;
      service: TypeResource;
    };
    agentChat: {
      message: {
        button: {
          primary: {
            hover: {
              background: string;
              border: string;
              foreground: string;
            };
            background: string;
            border: string;
            foreground: string;
          };
          secondary: {
            hover: {
              background: string;
              border: string;
              foreground: string;
            };
            background: string;
            border: string;
            foreground: string;
          };
          destructive: {
            hover: {
              background: string;
              border: string;
              foreground: string;
            };
            background: string;
            border: string;
            foreground: string;
          };
        };
        agent: {
          background: string;
          backgroundHover: string;
          backgroundSelected: string;
          border: string;
          foreground: {
            default: string;
            light: string;
          };
        };
        user: {
          background: string;
          border: string;
          foreground: {
            default: string;
            light: string;
          };
          queued: {
            background: string;
            border: string;
            foreground: {
              default: string;
              light: string;
              warning: string;
            };
          };
        };
      };
    };
  }
}

const sharedPalette = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  grey: {
    50: '#F8F8F8',
    100: '#EFEFEF',
    150: '#E6E6E6',
    200: '#DEDEDE',
    300: '#B2B2B2',
    400: '#989898',
    500: '#7E7E7E',
    600: '#656565',
    700: '#4C4C4C',
    750: '#424242',
    800: '#323232',
    850: '#272727',
    900: '#1B1B1B',
  },
  primary: {
    main: '#91E600',
    dark: '#76B900',
    light: '#E9FACC',
    contrastText: '#1B1B1B',
    100: '#E9FACC',
    500: '#91E600',
    600: '#76B900',
    700: '#537800',
    800: '#3A5C00',
  },
  error: {
    main: '#FF6C52',
    dark: '#CC5642',
    light: '#FFC4BA',
    contrastText: '#ffffff',
    100: '#FFE2DC',
    200: '#FFC4BA',
    500: '#FF6C52',
    600: '#CC5642',
    700: '#994131',
  },
  // updatedError: {
  //   main: '#EF4444',      // Balanced for both modes
  //   dark: '#DC2626',      // Darker red
  //   light: '#FCA5A5',     // Light red
  //   contrastText: '#ffffff',
  //   // Dark mode text colors
  //   100: '#FEE2E2',       // Lightest (use on dark backgrounds)
  //   200: '#FECACA',
  //   400: '#F87171',       // Light red (use on dark backgrounds)
  //   500: '#EF4444',       // Main
  //   600: '#DC2626',       // Dark red (use on light backgrounds)
  //   700: '#B91C1C',       // Darker red (use on light backgrounds)
  //   800: '#991B1B',       // Darkest
  // }
  // updatedWarning:{
  //   main: '#F59E0B',      // Balanced amber
  //   dark: '#D97706',      // Darker amber
  //   light: '#FDE68A',     // Light yellow
  //   contrastText: '#1B1B1B',
  //   // Text colors for different backgrounds
  //   100: '#FEF3C7',       // Lightest
  //   200: '#FDE68A',
  //   400: '#FBBF24',       // Light amber (use on dark backgrounds)
  //   500: '#F59E0B',       // Main
  //   600: '#D97706',       // Dark amber (use on light backgrounds)
  //   700: '#B45309',       // Darker amber (use on light backgrounds)
  //   800: '#92400E',       // Darkest
  // }
};

export const lightPalette = {
  ...sharedPalette,
  page: {
    headerText: {
      default: sharedPalette.grey[900],
      accent: sharedPalette.primary[700],
    },
  },
  warning: {
    main: '#CA8A04',
    dark: '#A16207',
    light: '#FEF9C3',
    contrastText: '#1B1B1B',
    100: '#FEF9C3',
    500: '#EAB308',
    600: '#CA8A04',
    700: '#A16207',
    800: '#854D0E',
  },
  shadow: {
    dropdown: '0px 0px 20px 0px rgba(0, 0, 0, 0.1)',
    modal: '0px 0px 20px 0px rgba(0, 0, 0, 0.25)',
  },
  modal: {
    background: {
      card: sharedPalette.grey[200],
    },
  },
  text: {
    primary: sharedPalette.grey[900],
    secondary: sharedPalette.common.white,
    light: sharedPalette.grey[700],
    paper: sharedPalette.grey[900],
    contrast: sharedPalette.grey[900],
    accent: sharedPalette.primary[700],
  },
  icon: {
    default: sharedPalette.grey[500],
    error: '#DC2626',
    info: '#7F92D4',
    success: '#2d7a2d',
    warning: '#B45309',
    disabled: sharedPalette.grey[300],
    sideNav: sharedPalette.grey[500],
  },
  header: {
    primary: sharedPalette.common.black,
    secondary: sharedPalette.grey[800],
  },
  background: {
    default: sharedPalette.grey[100],
    paper: sharedPalette.common.white,
    paperCard: sharedPalette.grey[50],
    paperHeader: sharedPalette.grey[300],
  },
  foreground: {
    default: {
      default: sharedPalette.grey[900],
      light: sharedPalette.grey[600],
      warning: '#B45309',
      error: '#DC2626',
    },
    paper: {
      default: sharedPalette.grey[900],
      light: sharedPalette.grey[600],
      accent: sharedPalette.primary[700],
      warning: '#B45309',
      error: '#DC2626',
    },
    paperCard: {
      default: sharedPalette.grey[900],
      light: sharedPalette.grey[600],
      warning: '#B45309',
      error: '#DC2626',
    },
    paperHeader: {
      warning: '#92400E',
      error: '#B91C1C',
    },
  },
  agentChat: {
    message: {
      button: {
        primary: {
          hover: {
            background: sharedPalette.primary.dark,
            border: sharedPalette.primary.main,
            foreground: sharedPalette.grey[800],
          },
          background: sharedPalette.primary.main,
          border: sharedPalette.primary.dark,
          foreground: sharedPalette.grey[800],
        },
        secondary: {
          hover: {
            background: sharedPalette.grey[200],
            border: sharedPalette.grey[700],
            foreground: sharedPalette.grey[900],
          },
          background: sharedPalette.grey[100],
          border: sharedPalette.grey[500],
          foreground: sharedPalette.grey[900],
        },
        destructive: {
          hover: {
            background: '#B91C1C',
            border: '#DC2626',
            foreground: sharedPalette.common.white,
          },
          background: '#DC2626',
          border: '#B91C1C',
          foreground: sharedPalette.common.white,
        },
      },
      agent: {
        background: sharedPalette.common.white,
        backgroundHover: sharedPalette.grey[200],
        backgroundSelected: sharedPalette.grey[300],
        border: sharedPalette.grey[200],
        foreground: {
          default: sharedPalette.grey[900],
          light: sharedPalette.grey[600],
        },
      },
      user: {
        background: sharedPalette.primary.main,
        border: sharedPalette.primary.dark,
        foreground: {
          default: sharedPalette.primary.contrastText,
          light: alpha(sharedPalette.grey[700], 0.9),
        },
        queued: {
          background: sharedPalette.grey[200],
          border: '#FACC15',
          foreground: {
            default: sharedPalette.grey[800],
            light: sharedPalette.grey[700],
            warning: sharedPalette.grey[850],
          },
        },
      },
    },
  },
  backgroundHover: {
    default: sharedPalette.grey[50],
    paper: sharedPalette.grey[100],
    paperCard: sharedPalette.grey[100],
  },
  input: {
    background: {
      default: sharedPalette.common.white,
      hover: sharedPalette.grey[50],
      active: sharedPalette.common.white,
      disabled: sharedPalette.grey[100],
    },
    border: {
      default: sharedPalette.grey[500],
      hover: sharedPalette.grey[500],
      active: sharedPalette.grey[500],
      disabled: sharedPalette.grey[200],
      error: sharedPalette.error.dark,
    },
    text: {
      default: sharedPalette.grey[900],
      disabled: sharedPalette.grey[500],
      error: sharedPalette.error.dark,
      placeholder: sharedPalette.grey[500],
    },
    icon: {
      default: sharedPalette.grey[500],
      disabled: sharedPalette.grey[300],
    },
    label: {
      default: sharedPalette.grey[900],
      error: sharedPalette.error.dark,
      disabled: sharedPalette.grey[500],
    },
  },
  button: {
    background: {
      primary: sharedPalette.grey[900],
      secondary: sharedPalette.grey[100],
      accent: sharedPalette.primary.main,
      landing: sharedPalette.grey[50],
    },
    border: {
      primary: sharedPalette.common.black,
      secondary: sharedPalette.grey[500],
      accent: sharedPalette.primary.dark,
      landing: sharedPalette.grey[300],
    },
    hover: {
      primary: sharedPalette.grey[700],
      secondary: sharedPalette.grey[50],
      accent: sharedPalette.common.white,
      landing: sharedPalette.grey[200],
    },
    icon: {
      primary: sharedPalette.grey[200],
      secondary: sharedPalette.grey[500],
      accent: sharedPalette.grey[900],
      landing: sharedPalette.grey[900],
    },
    text: {
      primary: sharedPalette.common.white,
      secondary: sharedPalette.grey[900],
      accent: sharedPalette.grey[900],
      landing: sharedPalette.grey[900],
    },
    outline: sharedPalette.grey[500],
  },
  toggle: {
    thumb: sharedPalette.common.white,
    track: {
      unchecked: {
        default: sharedPalette.grey[900],
        hover: sharedPalette.grey[500],
        border: sharedPalette.grey[500],
      },
      checked: {
        default: sharedPalette.primary.dark,
        hover: sharedPalette.primary.main,
        border: sharedPalette.primary[800],
      },
    },
  },
  radio: {
    fill: {
      checked: sharedPalette.primary.main,
      unchecked: sharedPalette.common.white,
    },
    default: sharedPalette.grey[500],
    hover: sharedPalette.grey[900],
    focus: sharedPalette.grey[500],
    icon: {
      default: sharedPalette.grey[500],
      hover: sharedPalette.grey[300],
      checked: sharedPalette.grey[700],
      checkedBackground: sharedPalette.grey[300],
    },
  },
  checkbox: {
    checked: {
      background: sharedPalette.primary.dark,
      border: sharedPalette.primary[800],
      hover: sharedPalette.primary.main,
    },
    unchecked: {
      background: sharedPalette.grey[100],
      border: sharedPalette.grey[500],
      hover: sharedPalette.common.white,
    },
  },
  menu: {
    background: sharedPalette.common.white,
    border: sharedPalette.grey[500],
    item: {
      hover: sharedPalette.grey[200],
      hoverText: sharedPalette.common.black,
      selected: sharedPalette.grey[200],
    },
  },
  progress: {
    track: sharedPalette.grey[200],
    default: sharedPalette.grey[700],
    error: sharedPalette.error.main,
    warning: '#CA8A04',
  },
  tab: {
    unselected: {
      default: sharedPalette.grey[600],
      hover: sharedPalette.primary[800],
    },
    selected: {
      default: sharedPalette.primary[700],
      hover: sharedPalette.primary[800],
    },
    disabled: sharedPalette.grey[700],
    indicator: sharedPalette.primary[800],
    divider: sharedPalette.grey[400],
  },
  link: {
    default: sharedPalette.primary.dark,
    hover: sharedPalette.primary[800],
  },
  graph: {
    link: {
      default: sharedPalette.grey[900],
      inactive: sharedPalette.grey[400],
    },
    port: {
      linked: sharedPalette.grey[900],
      unlinked: sharedPalette.grey[500],
      background: {
        input: sharedPalette.grey[100],
        inputHover: sharedPalette.grey[200],
        output: sharedPalette.grey[200],
      },
    },
    node: {
      background: `${sharedPalette.common.white}BF`,
      border: {
        default: sharedPalette.grey[400],
        hover: sharedPalette.grey[700],
        active: sharedPalette.grey[900],
        error: sharedPalette.error.main,
      },
    },
    drawer: {
      background: sharedPalette.grey[150],
    },
  },
  table: {
    header: sharedPalette.grey[700],
    cell: {
      border: sharedPalette.grey[200],
      text: sharedPalette.grey[900],
    },
    row: {
      hover: sharedPalette.grey[100],
      selected: sharedPalette.grey[200],
    },
    modeButton: {
      hover: sharedPalette.grey[100],
      border: {
        default: sharedPalette.grey[200],
        selected: sharedPalette.grey[300],
      },
      icon: {
        default: sharedPalette.grey[500],
        hover: sharedPalette.grey[700],
        selected: sharedPalette.grey[900],
      },
      background: {
        default: sharedPalette.common.white,
        selected: sharedPalette.grey[200],
        hover: sharedPalette.grey[100],
      },
    },
  },
  slider: {
    thumb: {
      border: sharedPalette.grey[900],
      background: {
        default: sharedPalette.common.white,
        hover: sharedPalette.grey[200],
      },
      active: '0 0 0 7px rgba(27, 27, 27, 0.1)',
    },
    rail: {
      default: sharedPalette.grey[400],
      disabled: sharedPalette.grey[100],
    },
    track: {
      default: sharedPalette.grey[900],
      disabled: sharedPalette.grey[100],
    },
  },
  fileUpload: {
    background: {
      default: sharedPalette.common.white,
      hover: sharedPalette.grey[50],
    },
    border: {
      default: sharedPalette.grey[500],
      drag: sharedPalette.primary[600],
    },
    file: {
      background: sharedPalette.grey[100],
      color: {
        primary: sharedPalette.grey[900],
        secondary: sharedPalette.grey[600],
      },
    },
  },
  resource: {
    workspace: {
      foreground: '#2d7a2d',
      background: '#e6ffe6',
      border: '#99e699',
      hoverBorder: '#66cc66',
    },
    organization: {
      foreground: '#7c3aed',
      background: '#f3e8ff',
      border: '#c4b5fd',
      hoverBorder: '#a78bfa',
    },
    user: {
      foreground: '#c2410c',
      background: '#fff4ed',
      border: '#fdba74',
      hoverBorder: '#fb923c',
    },
    platform: {
      foreground: '#1d4ed8',
      background: '#dbeafe',
      border: '#93c5fd',
      hoverBorder: '#60a5fa',
    },
    service: {
      foreground: '#0f766e',
      background: '#ccfbf1',
      border: '#5eead4',
      hoverBorder: '#2dd4bf',
    },
  },
};

export const darkPalette = {
  ...sharedPalette,
  page: {
    headerText: {
      default: sharedPalette.grey[300],
      accent: sharedPalette.primary.dark,
    },
  },
  warning: {
    main: '#FACC15',
    dark: '#854D0E',
    light: '#FEF9C3',
    contrastText: '#1B1B1B',
    100: '#FEF9C3',
    500: '#EAB308',
    600: '#CA8A04',
    700: '#A16207',
    800: '#854D0E',
  },
  shadow: {
    dropdown: '0px 0px 20px 0px rgba(0, 0, 0, 0.1)',
    modal: '0px 0px 20px 0px rgba(0, 0, 0, 0.255)',
  },
  modal: {
    background: {
      card: sharedPalette.grey[750],
    },
  },
  text: {
    primary: sharedPalette.grey[300],
    secondary: sharedPalette.grey[900],
    light: sharedPalette.grey[300],
    paper: sharedPalette.grey[50],
    contrast: sharedPalette.common.white,
    accent: sharedPalette.primary.main,
  },
  icon: {
    default: sharedPalette.grey[500],
    error: '#F87171',
    info: '#7F92D4',
    success: '#91e600',
    warning: '#FBBF24',
    disabled: sharedPalette.grey[700],
    sideNav: sharedPalette.grey[300],
  },
  header: {
    primary: sharedPalette.common.black,
    secondary: sharedPalette.grey[800],
  },
  background: {
    default: sharedPalette.grey[900],
    paper: sharedPalette.grey[800],
    paperCard: sharedPalette.grey[850],
    paperHeader: sharedPalette.common.black,
  },
  foreground: {
    default: {
      default: sharedPalette.grey[200],
      light: sharedPalette.grey[400],
      warning: '#FBBF24',
      error: '#F87171',
    },
    paper: {
      default: sharedPalette.grey[50],
      light: sharedPalette.grey[300],
      accent: sharedPalette.primary[500],
      warning: '#FCD34D',
      error: '#F87171',
    },
    paperCard: {
      default: sharedPalette.grey[200],
      light: sharedPalette.grey[400],
      warning: '#FBBF24',
      error: '#F87171',
    },
    paperHeader: {
      warning: '#FDE68A',
      error: '#FECACA',
    },
  },
  agentChat: {
    message: {
      button: {
        primary: {
          hover: {
            background: sharedPalette.primary.main,
            border: sharedPalette.primary.dark,
            foreground: sharedPalette.grey[800],
          },
          background: 'transparent',
          border: sharedPalette.primary.main,
          foreground: sharedPalette.primary.main,
        },
        secondary: {
          hover: {
            background: sharedPalette.grey[100],
            border: sharedPalette.grey[500],
            foreground: sharedPalette.grey[900],
          },
          background: 'transparent',
          border: sharedPalette.grey[300],
          foreground: sharedPalette.grey[300],
        },
        destructive: {
          hover: {
            background: '#EF4444',
            border: '#DC2626',
            foreground: sharedPalette.grey[800],
          },
          background: 'transparent',
          border: '#EF4444',
          foreground: '#EF4444',
        },
      },
      agent: {
        background: sharedPalette.grey[800],
        backgroundHover: sharedPalette.grey[600],
        backgroundSelected: sharedPalette.grey[700],
        border: sharedPalette.grey[700],
        foreground: {
          default: sharedPalette.grey[200],
          light: sharedPalette.grey[300],
        },
      },
      user: {
        background: sharedPalette.primary.main,
        border: sharedPalette.primary.dark,
        foreground: {
          default: sharedPalette.primary.contrastText,
          light: alpha(sharedPalette.grey[700], 0.9),
        },
        queued: {
          background: sharedPalette.grey[700],
          border: '#FACC15',
          foreground: {
            default: sharedPalette.grey[200],
            light: sharedPalette.grey[300],
            warning: sharedPalette.grey[850],
          },
        },
      },
    },
  },
  backgroundHover: {
    default: sharedPalette.grey[700],
    paper: sharedPalette.grey[700],
    paperCard: sharedPalette.grey[700],
  },
  input: {
    background: {
      default: sharedPalette.common.black,
      hover: sharedPalette.grey[900],
      active: sharedPalette.common.black,
      disabled: sharedPalette.grey[800],
    },
    border: {
      default: sharedPalette.grey[700],
      hover: sharedPalette.grey[700],
      active: sharedPalette.grey[700],
      disabled: sharedPalette.grey[900],
      error: sharedPalette.error.main,
    },
    text: {
      default: sharedPalette.grey[200],
      disabled: sharedPalette.grey[600],
      error: sharedPalette.error.main,
      placeholder: sharedPalette.grey[500],
    },
    icon: {
      default: sharedPalette.grey[500],
      disabled: sharedPalette.grey[700],
    },
    label: {
      default: sharedPalette.grey[200],
      error: sharedPalette.error.main,
      disabled: sharedPalette.grey[500],
    },
  },
  button: {
    background: {
      primary: sharedPalette.grey[200],
      secondary: sharedPalette.grey[900],
      accent: sharedPalette.primary.main,
      landing: sharedPalette.grey[900],
    },
    border: {
      primary: sharedPalette.grey[200],
      secondary: sharedPalette.common.black,
      accent: sharedPalette.primary.dark,
      landing: sharedPalette.grey[300],
    },
    hover: {
      primary: sharedPalette.grey[400],
      secondary: sharedPalette.grey[700],
      accent: sharedPalette.common.white,
      landing: sharedPalette.grey[700],
    },
    icon: {
      primary: sharedPalette.grey[500],
      secondary: sharedPalette.grey[200],
      accent: sharedPalette.grey[900],
      landing: sharedPalette.grey[500],
    },
    text: {
      primary: sharedPalette.grey[900],
      secondary: sharedPalette.common.white,
      accent: sharedPalette.grey[900],
      landing: sharedPalette.grey[200],
    },
    outline: sharedPalette.grey[500],
  },
  toggle: {
    thumb: sharedPalette.common.black,
    track: {
      unchecked: {
        default: sharedPalette.grey[400],
        hover: sharedPalette.grey[500],
        border: sharedPalette.grey[200],
      },
      checked: {
        default: sharedPalette.primary.main,
        hover: sharedPalette.primary.dark,
        border: sharedPalette.primary[100],
      },
    },
  },
  radio: {
    fill: {
      checked: sharedPalette.primary.main,
      unchecked: sharedPalette.common.black,
    },
    default: sharedPalette.grey[500],
    hover: sharedPalette.grey[200],
    focus: sharedPalette.grey[500],
    icon: {
      default: sharedPalette.grey[500],
      hover: sharedPalette.grey[400],
      checked: sharedPalette.grey[300],
      checkedBackground: sharedPalette.grey[700],
    },
  },
  checkbox: {
    checked: {
      background: sharedPalette.primary.main,
      border: sharedPalette.primary[800],
      hover: sharedPalette.primary.dark,
    },
    unchecked: {
      background: sharedPalette.common.black,
      border: sharedPalette.grey[500],
      hover: sharedPalette.grey[900],
    },
  },
  menu: {
    background: sharedPalette.common.black,
    border: sharedPalette.grey[500],
    item: {
      hover: sharedPalette.grey[800],
      selected: sharedPalette.grey[700],
      hoverText: sharedPalette.common.white,
    },
  },
  progress: {
    track: sharedPalette.grey[900],
    default: sharedPalette.grey[200],
    error: sharedPalette.error.main,
    warning: '#FACC15',
  },
  tab: {
    unselected: {
      default: sharedPalette.grey[200],
      hover: sharedPalette.primary.dark,
    },
    selected: {
      default: sharedPalette.primary.main,
      hover: sharedPalette.primary.dark,
    },
    disabled: sharedPalette.grey[600],
    // indicator: sharedPalette.grey[500],
    indicator: sharedPalette.primary.main,
    divider: sharedPalette.grey[600],
  },
  link: {
    default: sharedPalette.primary.main,
    hover: sharedPalette.primary.dark,
  },
  graph: {
    link: {
      default: sharedPalette.grey[500],
      inactive: sharedPalette.grey[700],
    },
    port: {
      linked: sharedPalette.common.white,
      unlinked: sharedPalette.grey[500],
      background: {
        input: sharedPalette.grey[800],
        inputHover: sharedPalette.grey[700],
        output: sharedPalette.grey[800],
      },
    },
    node: {
      background: `${sharedPalette.grey[900]}BF`,
      border: {
        default: sharedPalette.grey[700],
        hover: sharedPalette.grey[500],
        active: sharedPalette.grey[200],
        error: sharedPalette.error.main,
      },
    },
    drawer: {
      background: sharedPalette.grey[750],
    },
  },
  table: {
    header: sharedPalette.grey[500],
    cell: {
      border: sharedPalette.grey[700],
      text: sharedPalette.grey[200],
    },
    row: {
      hover: sharedPalette.grey[700],
      selected: sharedPalette.grey[600],
    },
    modeButton: {
      hover: sharedPalette.grey[900],
      border: {
        default: sharedPalette.grey[800],
        selected: sharedPalette.grey[700],
      },
      icon: {
        default: sharedPalette.grey[500],
        hover: sharedPalette.grey[400],
        selected: sharedPalette.grey[200],
      },
      background: {
        default: sharedPalette.grey[800],
        selected: sharedPalette.grey[600],
        hover: sharedPalette.grey[700],
      },
    },
  },
  slider: {
    thumb: {
      border: sharedPalette.grey[200],
      background: {
        default: sharedPalette.grey[900],
        hover: sharedPalette.grey[700],
      },
      active: '0 0 0 7px rgba(255, 255, 255, 0.15)',
    },
    rail: {
      default: sharedPalette.grey[900],
      disabled: sharedPalette.grey[700],
    },
    track: {
      default: sharedPalette.grey[200],
      disabled: sharedPalette.grey[700],
    },
  },
  fileUpload: {
    background: {
      default: sharedPalette.grey[750],
      hover: sharedPalette.grey[700],
    },
    border: {
      default: sharedPalette.grey[500],
      drag: sharedPalette.primary.main,
    },
    file: {
      background: sharedPalette.grey[900],
      color: {
        primary: sharedPalette.grey[100],
        secondary: sharedPalette.grey[300],
      },
    },
  },
  resource: {
    workspace: {
      foreground: '#91e600',
      background: 'rgba(127, 255, 0, 0.2)',
      border: alpha('#91e600', 0.2),
      hoverBorder: alpha('#91e600', 0.4),
    },
    organization: {
      foreground: '#c084fc',
      background: 'rgba(168, 85, 247, 0.2)',
      border: 'rgba(168, 85, 247, 0.3)',
      hoverBorder: 'rgba(168, 85, 247, 0.5)',
    },
    user: {
      foreground: '#fb923c',
      background: 'rgba(249, 115, 22, 0.2)',
      border: 'rgba(249, 115, 22, 0.3)',
      hoverBorder: 'rgba(249, 115, 22, 0.5)',
    },
    platform: {
      foreground: '#60a5fa',
      background: alpha('#3b82f6', 0.2),
      border: alpha('#3b82f6', 0.3),
      hoverBorder: alpha('#3b82f6', 0.5),
    },
    service: {
      foreground: '#2dd4bf',
      background: alpha('#14b8a6', 0.2),
      border: alpha('#14b8a6', 0.3),
      hoverBorder: alpha('#14b8a6', 0.5),
    },
  },
};
